<template>
  <div class="page map-area-container h-full relative bg-neutral2" v-touch:start="onTouch">
    <WFMap
      :project="$WF_PROJECT"
      ref="map"
      :showPOILogo="true"
      @loaded="mapLoaded"
      class="h-full"
      :POIPopupEnabled="true"
    >
      <template v-slot:poi="slotProps">
        <PopUp
          :poi="slotProps.poi"
          @showPath="showPath"
          @close="closePopup"
          class="th-shadow-xl rounded-xl"
        ></PopUp>
      </template>
    </WFMap>
    <!-- Left side shortcuts - Portrait only -->
    <div v-if="portrait" class="absolute bottom-38 left-24 left-menu flex flex-col gap-12">
      <WFShortcutsMenu 
        class="th-btn-group flex flex-col"
        :showLabel="false"
      ></WFShortcutsMenu>
    </div>
    <div class="absolute bottom-38 right-24 right-menu flex flex-col gap-16">
      <div class="th-btn-group flex flex-col">
        <WFYAH :showLabel="false" class="wf-list-item"></WFYAH>
      </div>
      <WFShortcutsMenu 
        v-if="landscape"
        class="th-btn-group"
        :showLabel="false"
      ></WFShortcutsMenu>
      <!-- Only render floor menu after map is loaded -->
      <WFFloorsMenu
        v-if="mapIsLoaded"
        ref="floorsMenu"
        class="map-floors-menu th-btn-group"
        :currentFloor="currentFloor"
      />
      <WFZoomMenu class="th-btn-group flex flex-col gap-4"></WFZoomMenu>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import PopUp from "./PopUp.vue";

export default {
  components: {
    PopUp,
  },
  computed: {
    ...mapState(["currentPOI", "log"]),
    ...mapState("wf", ["language", "currentFloor", "reset", "portrait", "landscape"]),
  },
  data() {
    return {
      generalInfo: false,
      busTimes: false,
      mapIsLoaded: false,
    };
  },
  watch: {
    currentPOI() {
      console.log("currentPOI", this.currentPOI, this.$refs.map);
      if (this.currentPOI && this.$refs.map) {
        this.$refs.map.showPOIPopup(this.currentPOI, 320);
        this.busTimes = false;
        this.generalInfo = false;
      }
    },
    reset() {
      this.busTimes = false;
      this.generalInfo = false;
    },
    currentFloor() {
      // Scroll to active floor when it changes
      this.$nextTick(() => {
        this.scrollToActiveFloor();
      });
    }
  },
  methods: {
    scrollToBottom() {
      const floorList = document.querySelector('.wf-floors-menu .wf-list');
      if (floorList) {
        floorList.scrollTop = floorList.scrollHeight;
      }
    },
    scrollToActiveFloor() {
      const floorList = document.querySelector('.wf-floors-menu .wf-list');
      const activeFloor = document.querySelector('.wf-floors-menu .wf-active');
      
      if (floorList && activeFloor) {
        activeFloor.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    },
    mapLoaded() {
      this.mapIsLoaded = true;
      
      this.$nextTick(() => {
        // FORCE populate floors into store
        if (this.$wayfinder && this.$wayfinder.building && this.$wayfinder.building.floors) {
          const floorsObj = this.$wayfinder.building.floors;
          
          // Convert floors object to array and SORT by index (ascending)
          const floorsArray = Object.values(floorsObj).sort((a, b) => a.index - b.index);
          
          console.log('=== FIXING FLOORS ===');
          console.log('Floors array sorted:', floorsArray);
          
          // Set floors directly on state
          if (this.$store._mutations['wf/SET_FLOORS']) {
            this.$store.commit('wf/SET_FLOORS', floorsArray);
          } else {
            // Fallback to direct mutation if mutation doesn't exist
            this.$store.state.wf.floors = floorsArray;
          }
          
          console.log('Floors now in store:', this.$store.state.wf.floors);
        }
        
        // Scroll to bottom after floors render
        setTimeout(() => {
          this.scrollToBottom();
        }, 1000);
      });
      
      this.$wayfinder.events.on("map-click", (poi) => {
        this.$store.dispatch(
          "setPOI",
          this.$store.getters["wf/freezePOI"](poi)
        );
      });

      this.$wayfinder.events.on("path-start", () => {
        this.busTimes = false;
        this.generalInfo = false;
      });

      this.$emit("loaded");
    },
    getPOIImage(poi) {
      if (poi.getBackgroundUrl()) return poi.getBackgroundUrl();
      else if (poi.getIconUrl()) {
        return poi.getIconUrl();
      } else {
        return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
      }
    },
    isPOIImage(poi) {
      if (poi.getBackgroundUrl()) return "image-background";
      else if (poi.getIconUrl()) return "image-logo";
      else return "image-none";
    },
    closePOI() {
      console.log("closePOI");
      this.$store.dispatch("setPOI", null);
    },
    showPath() {
      if (this.currentPOI) {
        this.busTimes = false;
        this.generalInfo = false;
        this.$wayfinder.showPath(this.currentPOI.getNode(), this.currentPOI);
      }
    },
    closePopup() {
      console.log("closePopup", this.$refs.map);
      if (this.$refs.map) {
        this.$refs.map.hidePOIPopup();
      }
    },
    showGeneralInfo() {
      return () => {
        this.generalInfo = !this.generalInfo;
        this.busTimes = false;
        this.$refs.map.hidePOIPopup();
      };
    },
    showBusTimes() {
      return () => {
        this.busTimes = !this.busTimes;
        this.generalInfo = false;
        this.$refs.map.hidePOIPopup();
      };
    },
    onTouch() {
      this.$wayfinder.run();
    },
  },
};
</script>

<style lang="postcss">
/* ===== RESPONSIVE FLOOR MENU FIX ===== */

/* Hide language switcher from Wayfinder map (bottom-left) */
.wf-list.wf-list-count-2 {
  display: none !important;
}

/* Hide scrollbar - WebKit browsers */
.wf-floors-menu .wf-list::-webkit-scrollbar {
  width: 0 !important;
  display: none !important;
}

/* Floor menu container - display floors bottom to top */
.wf-floors-menu .wf-list {
  display: flex !important;
  flex-direction: column-reverse !important; /* Bottom to top */
  max-height: calc((56px + 0.2rem) * 3 + 0.8rem);
  overflow-y: auto;
  scroll-behavior: smooth;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

/* Add spacing between floor buttons */
.wf-floors-menu .wf-list-item {
  margin-bottom: 0.4rem;
}

.wf-floors-menu .wf-list-item:last-child {
  margin-bottom: 0;
}

/* Floor button styling - BASE (mobile & landscape) */
.map-floors-menu .wf-list-item {
  min-height: 46px !important;
  width: 46px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background-color: white !important;
  color: #333333 !important;
  border: 1px solid #e5e5e5 !important;
  border-radius: 1rem !important;
  font-size: 1.8rem !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  
}

.wf-floors-menu .wf-list-item {
    margin-bottom: 0.1rem !important;
  }

/* Active floor button */
.map-floors-menu .wf-list-item.wf-active {
  background-color: var(--color-primary) !important;
  color: white !important;
  border-color: var(--color-primary) !important;
  transform: scale(1.1) !important;
}

/* Hover effect */
.map-floors-menu .wf-list-item:hover {
  background-color: #f4f4f4 !important;
}

.map-floors-menu .wf-list-item.wf-active:hover {
  background-color: var(--color-primary) !important;
}

/* ===== PORTRAIT MODE FIXES ===== */
@media (orientation: portrait) and (min-width: 768px) {
  /* Make floor buttons bigger in portrait */
  .map-floors-menu .wf-list-item {
    min-height: 56px !important;
    width: 56px !important;
    font-size: 2rem !important;
    border-radius: 1.2rem !important;
  }
  
  /* Increase spacing between buttons in portrait */
  .wf-floors-menu .wf-list-item {
    margin-bottom: 0.2rem !important;
  }
  
  /* Adjust max height for portrait (show 3 buttons) */
  .wf-floors-menu .wf-list {
    max-height: calc((56px + 0.8rem) * 3) !important;
  }
  
  /* Center align the floor menu container in portrait */
  .map-floors-menu {
    align-items: center !important;
  }
}

/* ===== MOBILE SPECIFIC (extra small screens) ===== */
@media (max-width: 480px) {
  /* Slightly smaller buttons on very small screens */
  .map-floors-menu .wf-list-item {
    min-height: 42px !important;
    width: 42px !important;
    font-size: 1.6rem !important;
    margin-left: 2px;
  }
}

/* ===== END FLOOR MENU FIX ===== */

.wf-browser {
  user-select: none;
}

.wf-browser iframe {
  user-select: none;
}

.wf-poi-popup {
  border-radius: 1.2rem;
  padding: 0 !important;
  width: 27rem !important;
  box-shadow: 0px 24px 24px 0px rgba(0, 0, 0, 0.09);
}

.general-info {
  background-color: rgba(255, 255, 255, 0.4);
}

.wf-shortcuts-menu .wf-list {
  display: flex;
  flex-direction: column;
}

.wf-shortcuts-menu .wf-list-item:active {
  background-color: var(--th-primary);

  img {
    filter: invert(1);
  }
}

.wf-yah:active {
  background-color: var(--th-primary);
}
/* ===== 4K LANDSCAPE ONLY (3840 x 2160) ===== */
@media (min-width: 3840px) and (orientation: landscape) {
  /* Make the container wider and adjust positioning */
  .right-menu,
  .left-menu {
    gap: 2rem !important;
    width: auto !important; /* Allow container to grow with button size */
  }
  
  .right-menu {
    right: 4rem !important; /* Move slightly away from edge */
  }
  
  /* All control buttons - bigger and consistent sizing */
  .map-floors-menu .wf-list-item,
  .wf-shortcuts-menu .wf-list-item,
  .wf-yah,
  .wf-zoom-menu .wf-item,
  .wf-zoom-in,
  .wf-zoom-out {
    min-height: 120px !important;
    width: 130px !important;
    min-width: 130px !important;
    font-size: 3.6rem !important;
    border-radius: 2rem !important;
    border-width: 2px !important;
  }
  
  /* Floor buttons specific styling */
  .wf-floors-menu .wf-list-item {
    margin-bottom: 0.8rem !important;
  }
  
  .wf-floors-menu .wf-list {
    max-height: calc((100px + 0.8rem) * 4) !important; /* Show 4 floors */
  }
  
  /* Floor change overlay text - 2x */
  .wf-map-path-text {
    font-size: 4.8rem !important;
    font-weight: 600 !important;
    line-height: 1.4 !important;
    padding: 2.4rem !important;
  }
  
  /* POI Popup - 2x */
  .wf-poi-popup {
    width: 54rem !important;
    border-radius: 2.4rem !important;
  }
}

/* ===== 4K PORTRAIT ONLY (2160 x 3840) ===== */
@media (min-width: 2160px) and (orientation: portrait) {
  /* Container positioning and spacing - 2x */
  .right-menu,
  .left-menu {
    gap: 3.2rem !important;
    bottom: 7.6rem !important;
  }


  .right-menu {
    right: 4.8rem !important;
  }
  
  .left-menu {
    left: 4.8rem !important;
  }
  
  /* Floor buttons - 2x */
  .map-floors-menu .wf-list-item {
    min-height: 112px !important;
    width: 112px !important;
    font-size: 4rem !important;
    border-radius: 2.4rem !important;
    border-width: 2px !important;
  }
  
  .wf-floors-menu .wf-list-item {
    margin-bottom: 0.4rem !important;
  }
  
  .wf-floors-menu .wf-list {
    max-height: calc((112px + 0.4rem) * 3) !important;
  }
  
  /* Shortcuts buttons - 2x */
  .right-menu .wf-shortcuts-menu .wf-list-item,
  .left-menu .wf-shortcuts-menu .wf-list-item {
    min-height: 112px !important;
    width: 112px !important;
  }
  
  /* YAH button - 2x */
  .right-menu .wf-yah,
  .left-menu .wf-yah {
    min-height: 112px !important;
    width: 112px !important;
  }
  
  /* Zoom buttons - 2x */
  .wf-zoom-menu .wf-item,
  .wf-zoom-in,
  .wf-zoom-out {
    min-height: 112px !important;
    width: 112px !important;
    font-size: 6.8rem !important;
  }
  
  /* Floor change overlay text - AGGRESSIVE - 2x */
  div.wf-map-path-text,
  .wf-map-path-text,
  .wf-map .wf-map-path-text,
  [class*="path-text"] {
    font-size: 4.8rem !important;
    font-weight: 600 !important;
    line-height: 1.4 !important;
    padding: 2.4rem !important;
  }
  
  /* POI Popup - 2x */
  .wf-poi-popup {
    width: 54rem !important;
    border-radius: 2.4rem !important;
    padding: 4rem !important;
  }
  
  /* POI name in popup - 2x */
  .wf-poi-popup .poi-name,
  .wf-poi-popup .text-24 {
    font-size: 4.8rem !important;
  }
  
  /* POI description/floor text - 2x */
  .wf-poi-popup .text-16,
  .wf-poi-popup .text-18,
  .wf-poi-popup .text-20,
  .wf-poi-popup p,
  .wf-poi-popup span {
    font-size: 3.6rem !important;
  }
  
  /* POI popup buttons - 2x */
  .wf-poi-popup button,
  .wf-poi-popup .wf-button {
    font-size: 3.6rem !important;
    padding: 2.4rem 3.6rem !important;
    min-height: 8rem !important;
  }
  
  /* POI logo in popup - 2x */
  .wf-poi-popup .wf-poi-logo,
  .wf-poi-popup img {
    max-width: 30rem !important;
    max-height: 20rem !important;
  }
}
</style>